// ════════════════════════════════════════════════════════════
// Incidencias — gestión de las peticiones de ayuda del SIS
// ════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, Alert, Button, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress, Grid
} from '@mui/material';
import { getIncidencias, resolverIncidencia, descargarAdjuntoIncidencia } from '../api';

const TIPOS: any = {
  error:    { label: 'Algo no funciona', color: 'error' },
  duda:     { label: 'Duda',             color: 'info' },
  mejora:   { label: 'Sugerencia',       color: 'secondary' },
  peticion: { label: 'Petición',         color: 'warning' },
};

export default function Incidencias() {
  const [datos, setDatos] = useState<any>({ items: [], es_admin: false, abiertas: 0 });
  const [vista, setVista] = useState(0);      // 0 abiertas · 1 resueltas · 2 todas
  const [cargando, setCargando] = useState(true);
  const [detalle, setDetalle] = useState<any>(null);
  const [respuesta, setRespuesta] = useState('');
  const [procesando, setProcesando] = useState(false);

  const cargar = () => {
    setCargando(true);
    getIncidencias()
      .then(setDatos).catch(() => setDatos({ items: [], es_admin: false, abiertas: 0 }))
      .finally(() => setCargando(false));
  };
  useEffect(() => { cargar(); }, []);

  const todas: any[] = datos.items || [];
  const abiertas = todas.filter((x: any) => x.estado === 'abierta');
  const resueltas = todas.filter((x: any) => x.estado !== 'abierta');
  const lista = vista === 0 ? abiertas : vista === 1 ? resueltas : todas;

  const resolver = async (estado: string) => {
    setProcesando(true);
    try {
      await resolverIncidencia(detalle.id, { estado, respuesta });
      setDetalle(null);
      setRespuesta('');
      cargar();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setProcesando(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, fontSize: { xs: '1.5rem', md: '2rem' } }}>
        🆘 Incidencias del SIS
      </Typography>

      <Tabs value={vista} onChange={(_, v) => setVista(v)} sx={{ mb: 2 }}>
        <Tab label={`Abiertas (${abiertas.length})`} />
        <Tab label={`Resueltas (${resueltas.length})`} />
        <Tab label="Todas" />
      </Tabs>

      {cargando ? <CircularProgress /> : lista.length === 0 ? (
        <Alert severity={vista === 0 ? 'success' : 'info'}>
          {vista === 0 ? 'No hay incidencias abiertas.' : 'No hay incidencias que mostrar.'}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {lista.map((x: any) => {
            const t = TIPOS[x.tipo] || { label: x.tipo, color: 'default' };
            return (
              <Grid item xs={12} md={6} key={x.id}>
                <Card sx={{ borderRadius: 3, height: '100%', cursor: 'pointer',
                  borderLeft: x.estado === 'abierta' ? '4px solid #f59e0b' : '4px solid #16a34a',
                  '&:hover': { boxShadow: 4 } }}
                  onClick={() => { setDetalle(x); setRespuesta(x.respuesta || ''); }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, gap: 1 }}>
                      <Typography sx={{ fontWeight: 700 }}>#{x.id}</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Chip size="small" label={t.label} color={t.color} variant="outlined" />
                        <Chip size="small" label={x.estado === 'abierta' ? 'Abierta' : 'Resuelta'}
                          color={x.estado === 'abierta' ? 'warning' : 'success'} />
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{
                      display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                      overflow: 'hidden', mb: 1 }}>
                      {x.descripcion}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {x.usuario_nombre} {x.usuario_apellidos || ''} · {(x.creado_en || '').slice(0, 16)}
                      {x.pantalla ? ` · ${x.pantalla}` : ''}
                      {x.archivo ? ' · 📎' : ''}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={!!detalle} maxWidth="sm" fullWidth
        onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') setDetalle(null); }}>
        {detalle && (
          <>
            <DialogTitle>Incidencia #{detalle.id} · {(TIPOS[detalle.tipo] || {}).label}</DialogTitle>
            <DialogContent>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                {detalle.usuario_nombre} {detalle.usuario_apellidos || ''} ({detalle.usuario_email || '—'})
                · {(detalle.creado_en || '').slice(0, 16)}
                {detalle.pantalla ? ` · pantalla ${detalle.pantalla}` : ''}
              </Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap', p: 1.5, borderRadius: 2, bgcolor: 'action.hover', mb: 2 }}>
                {detalle.descripcion}
              </Typography>
              {detalle.archivo && (
                <Button size="small" sx={{ mb: 2 }}
                  onClick={() => descargarAdjuntoIncidencia(detalle.id, detalle.archivo_nombre)}>
                  📎 Ver el adjunto
                </Button>
              )}
              {datos.es_admin ? (
                <TextField fullWidth multiline rows={3} label="Respuesta para el usuario"
                  value={respuesta} onChange={(e) => setRespuesta(e.target.value)} />
              ) : detalle.respuesta ? (
                <Alert severity="success">
                  <b>Respuesta:</b> {detalle.respuesta}
                </Alert>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetalle(null)}>Cerrar</Button>
              {datos.es_admin && detalle.estado === 'abierta' && (
                <Button variant="contained" disabled={procesando} sx={{ bgcolor: '#22c55e' }}
                  onClick={() => resolver('resuelta')}>
                  Marcar resuelta y avisar
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
